import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { waLink } from "../lib/whatsapp";

const NAV = [
  { to: "/", label: "Start" },
  { to: "/leistungen", label: "Leistungen" },
  { to: "/einsatzgebiete", label: "Einsatzgebiete" },
  { to: "/besichtigung", label: "Besichtigung" },
  { to: "/kontakt", label: "Kontakt" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled && !open;

  return (
    <div className="min-h-screen bg-white">
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 backdrop-blur-xl border-b ${
          solid ? "bg-[#145DA0] border-[#145DA0] shadow-md" : "bg-white/95 border-[#E2E8F0]"
        }`}
        style={{ transition: "background-color .3s ease, border-color .3s ease" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className={`nx-head text-2xl font-semibold tracking-[0.4em] ${solid ? "text-[#FFFFFF]" : "text-[#145DA0]"}`}
            style={{ transition: "color .3s ease" }}
          >
            NENOX
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => {
              const active = pathname === n.to;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`text-xs uppercase tracking-[0.2em] ${
                    solid
                      ? active
                        ? "text-[#FFFFFF] font-semibold"
                        : "text-[#DBEAFE] hover:text-[#FFFFFF]"
                      : active
                        ? "text-[#145DA0] font-semibold"
                        : "text-[#475569] hover:text-[#145DA0]"
                  }`}
                  style={{ transition: "color .2s ease" }}
                >
                  {n.label}
                </NavLink>
              );
            })}
            <Link to="/leistungen" data-testid="header-cta" className="nx-btn-action !px-6 !py-3">
              Angebot anfordern
            </Link>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 rounded-lg border ${
              solid ? "border-[#FFFFFF]/50 text-[#FFFFFF]" : "border-[#CBD5E1] text-[#145DA0]"
            }`}
            onClick={() => setOpen(!open)}
            aria-label="Menü"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#E2E8F0] px-6 py-6 space-y-4 bg-white" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="block text-sm uppercase tracking-[0.2em] text-[#475569] py-2"
              >
                {n.label}
              </Link>
            ))}
            <a href="tel:+4916099007777" className="nx-btn-ghost w-full">Anrufen</a>
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-menu-whatsapp" className="nx-btn-ghost w-full">
              <MessageCircle size={16} strokeWidth={1.9} /> WhatsApp
            </a>
            <Link to="/leistungen" onClick={() => setOpen(false)} className="nx-btn-action w-full">
              Angebot anfordern
            </Link>
          </div>
        )}
      </header>

      <main className={pathname.startsWith("/anfrage") ? "" : "pb-20 md:pb-0"}>{children}</main>

      {!pathname.startsWith("/anfrage") && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-3 border-t border-[#E2E8F0] bg-white/98 backdrop-blur-xl"
          style={{ boxShadow: "0 -4px 16px -4px rgba(15,23,42,0.12)" }}
          data-testid="mobile-action-bar"
        >
          <a
            href="tel:+4916099007777"
            data-testid="mobile-call-btn"
            className="flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-[0.12em] text-[#145DA0] font-medium border-r border-[#E2E8F0]"
          >
            <Phone size={15} strokeWidth={1.8} /> Anrufen
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="mobile-whatsapp-btn"
            className="flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-[0.12em] text-[#128C7E] font-medium border-r border-[#E2E8F0]"
          >
            <MessageCircle size={15} strokeWidth={1.9} /> WhatsApp
          </a>
          <Link
            to="/leistungen"
            data-testid="mobile-quote-btn"
            className="flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-[0.12em] font-medium bg-[#FF7A00] text-white"
          >
            Angebot
          </Link>
        </div>
      )}

      <a
        href={waLink()}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-float"
        aria-label="Über WhatsApp schreiben"
        className="hidden md:inline-flex fixed bottom-8 right-8 z-40 items-center gap-3 pl-5 pr-6 py-4 rounded-full text-sm font-semibold text-white"
        style={{ background: "#25D366", boxShadow: "0 12px 28px -10px rgba(37,211,102,0.8)" }}
      >
        <MessageCircle size={20} strokeWidth={2} /> WhatsApp
      </a>

      <footer className="nx-dark mt-24" data-testid="site-footer" style={{ background: "#0F172A" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="nx-head text-xl tracking-[0.4em] text-[#FFFFFF]">NENOX</div>
            <p className="mt-5 text-sm text-zinc-400 max-w-sm leading-relaxed">
              Professionelle Dienstleistungen rund um Haus, Wohnung, Gewerbe und Außenbereich.
              Schnell. Zuverlässig. Professionell. 24/7 für Sie erreichbar.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5">Kontakt</div>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li><a href="tel:+4916099007777" className="flex items-center gap-3 hover:text-white"><Phone size={15} strokeWidth={1.5} /> 0160 99007777</a></li>
              <li><a href="tel:+491716171747" className="flex items-center gap-3 hover:text-white"><Phone size={15} strokeWidth={1.5} /> 0171 6171747</a></li>
              <li><a href={waLink()} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-3 hover:text-white"><MessageCircle size={15} strokeWidth={1.5} /> WhatsApp schreiben</a></li>
              <li className="text-white">24/7 erreichbar – auch am Wochenende</li>
              <li><a href="mailto:nenox@mail.de" className="flex items-center gap-3 hover:text-white"><Mail size={15} strokeWidth={1.5} /> nenox@mail.de</a></li>
              <li className="flex items-center gap-3"><MapPin size={15} strokeWidth={1.5} /> Weiherweg 14, 93051 Regensburg</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5">Rechtliches</div>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li><Link to="/impressum" data-testid="footer-impressum" className="hover:text-white">Impressum</Link></li>
              <li><Link to="/datenschutz" data-testid="footer-datenschutz" className="hover:text-white">Datenschutz</Link></li>
              <li><Link to="/admin/login" data-testid="footer-admin" className="hover:text-white">Admin-Bereich</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/15 py-6 text-center text-xs text-zinc-400">
          © {new Date().getFullYear()} NENOX · Alle Rechte vorbehalten
        </div>
      </footer>
    </div>
  );
};
